import React, { useState } from 'react';
import t from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './PasswordInput.scss';

const DefaultLabel = () => (
  <FormattedMessage
    id="passwordField.defaultLabel"
    defaultMessage="Enter your password"
    description="Default label preceding the password input"
  />
);

/**
 * @param {string} passValue - current password value
 * @param {function} onChange
 * @param {?string} error - text of the error, ignored if empty string
 * @param {?string} emailValue - value of the email, so the user can be redirected to the password reset
 * @param {string | JSX.Element | null} [label]
 * @param {?boolean} showForgotPassword - show/hide `Forgot password` link,
 * @param {?boolean} hideEmptyError - don't leave up a space for error,
 * @returns {JSX.Element}
 */
const PasswordInput = ({
  passValue,
  onChange,
  error,
  emailValue,
  label = <DefaultLabel />,
  showForgotPassword,
  hideEmptyError,
}) => {
  const intl = useIntl();

  const [isPasswordShown, setPasswordShown] = useState(false);
  const toggleButtonText = isPasswordShown
    ? intl.formatMessage({
        id: 'passwordField.hidePasswordButton',
        defaultMessage: 'Hide',
        description:
          'Button within the password field, that allows to hide current value behind asterisks, after it had been shown',
      })
    : intl.formatMessage({
        id: 'passwordField.showPasswordButton',
        defaultMessage: 'Show',
        description:
          'Button within the password field, that allows to show current value, instead of a set of asterisks',
      });
  const toggleInputType = () => setPasswordShown(!isPasswordShown);

  const isPasswordToggleShown = Boolean(passValue);

  const placeholder = intl.formatMessage({
    id: 'passwordField.placeholder',
    defaultMessage: 'Min 8 characters',
    description: 'A placeholder shown when the password field is empty',
  });

  return (
    <div className={styles.field}>
      {label ? <label htmlFor="password-input-field">{label}</label> : null}

      {showForgotPassword ? (
        <a
          href={`/enter/password/restore?email=${encodeURIComponent(emailValue)}`}
          tabIndex="-1"
          className={styles.forgot}
        >
          <FormattedMessage
            id="passwordField.forgotPassword"
            defaultMessage="Forgot password?"
            description="Link near the password field, redirecting to the password reset page"
          />
        </a>
      ) : null}

      <div className={styles.wrapper}>
        {isPasswordShown ? (
          <input
            id="password-input-field"
            value={passValue}
            placeholder={placeholder}
            name="password"
            className={isPasswordToggleShown ? styles.withToggle : ''}
            type="text"
            onChange={onChange}
          />
        ) : (
          <input
            id="password-input-field"
            value={passValue}
            placeholder={placeholder}
            name="password"
            className={isPasswordToggleShown ? styles.withToggle : ''}
            type="password"
            onChange={onChange}
          />
        )}

        <button
          type="button"
          tabIndex="-1"
          className={`${styles.showPassword} ${isPasswordToggleShown ? '' : styles.hidden}`}
          onClick={toggleInputType}
        >
          {toggleButtonText}
        </button>
      </div>

      {!hideEmptyError || error ? <div className={styles.error}>{error}</div> : null}
    </div>
  );
};

PasswordInput.propTypes = {
  passValue: t.string.isRequired,
  onChange: t.func.isRequired,
  error: t.string,
  emailValue: t.string,
  label: t.string,
  showForgotPassword: t.bool,
  hideEmptyError: t.bool,
};

export default PasswordInput;
